import React, {useRef,useState} from 'react';
import {Button} from '@/components/ui/button';

export const DIAGNOSTIC_REPAIR_POLICY='swiminn_diagnostic_drift_repair_v1';
export const diagnosticRepairStorageKey=(actorId:string,jobId:string)=>`category-research-diagnostic-repair:${actorId}:${jobId}`;
type Job={id:string;sectorId:number;siteId:string;continuationRepair?:{policy:string;continuationId:string}|null};
type Binding={siteId:string;requestId:string;continuationId:string;categories:Array<{categoryPath:string;categoryName:string}>};
type Apply=Binding&{snapshotHash:string;applyRequestId:string};
type Props={job:Job;actorId:string;apiUrl:string;authFetch:typeof fetch;onReceipt:(job:any)=>void};

export function SwiminnDiagnosticRepairPanel({job,actorId,apiUrl,authFetch,onReceipt}:Props) {
  const [prepared,setPrepared]=useState<(Binding&{snapshotHash:string})|null>(null),[pending,setPending]=useState<Apply|null>(null);
  const [busy,setBusy]=useState(false),[error,setError]=useState<string|null>(null),lock=useRef(false);
  const key=diagnosticRepairStorageKey(actorId,job.id);
  const endpoint=`${apiUrl}/category-characteristics/${job.sectorId}/batch-research/${job.id}`;
  const read=async(url:string,init?:RequestInit)=>{
    const response=await authFetch(url,init),data=await response.json();
    if(!response.ok||data.success!==true)throw new Error(data.error||`Research recovery failed: HTTP ${response.status}`);
    return data;
  };
  const guarded=async(action:()=>Promise<void>)=>{
    if(lock.current)return;lock.current=true;setBusy(true);setError(null);
    try{await action();}catch(cause){setError(cause instanceof Error?cause.message:String(cause));}
    finally{lock.current=false;setBusy(false);}
  };
  const prepare=()=>guarded(async()=>{
    const saved=localStorage.getItem(key);
    if(saved) {
      const value=JSON.parse(saved),request=value.request;
      if(value.jobId!==job.id||value.actorId!==actorId||request?.siteId!==job.siteId
        ||typeof request?.continuationId!=='string'||!request.continuationId
        ||(job.continuationRepair&&request.continuationId!==job.continuationRepair.continuationId)||!request?.applyRequestId||!request?.snapshotHash)
        throw new Error('Saved recovery is invalid. Check its outcome before making another request.');
      setPending(request);return;
    }
    const {job:current}=await read(endpoint);
    if(current?.id!==job.id||current.siteId!==job.siteId||current.sectorId!==job.sectorId||!current.requestIds?.[0]
      ||!Array.isArray(current.categories)||current.continuationRepair?.policy!==DIAGNOSTIC_REPAIR_POLICY
      ||current.continuationRepair.continuationId!==job.continuationRepair?.continuationId)
      throw new Error('The current receipt does not identify this saved recovery.');
    const binding={siteId:current.siteId,requestId:current.requestIds[0],categories:current.categories,continuationId:current.continuationRepair.continuationId};
    const {continuation}=await read(endpoint+'/continuation/repair/preview',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(binding)});
    if(continuation?.policy!==DIAGNOSTIC_REPAIR_POLICY||continuation.continuationId!==binding.continuationId
      ||continuation.maxNewProviderCalls!==45||!/^[a-f0-9]{64}$/.test(continuation.snapshotHash))
      throw new Error('The server returned an incomplete recovery preview.');
    setPrepared({...binding,snapshotHash:continuation.snapshotHash});
  });
  const apply=()=>guarded(async()=>{
    if(!pending&&!prepared)throw new Error('Review the saved recovery first.');
    const request=pending||{...prepared!,applyRequestId:crypto.randomUUID()};
    localStorage.setItem(key,JSON.stringify({jobId:job.id,actorId,request}));setPending(request);
    const {continuation}=await read(endpoint+'/continuation/repair/apply',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(request)});
    if(continuation?.jobId!==job.id||continuation.id!==request.continuationId||!continuation.repairId)
      throw new Error('Recovery acknowledgement is incomplete. Check this same saved request.');
    const {job:current}=await read(endpoint);
    if(current?.id!==job.id||current.siteId!==job.siteId||current.sectorId!==job.sectorId)throw new Error('Research status returned a different job.');
    localStorage.removeItem(key);setPending(null);setPrepared(null);onReceipt(current);
  });
  return <div className="mt-3 space-y-2 rounded border p-3">
    <p className="text-sm">Resume the saved Wetsuits continuation using its existing approval. Confirmed responses are kept. Earlier unknown outcomes and costs remain unknown.</p>
    <p className="text-sm">The existing limit remains 45 new provider calls, including replacements and remaining trial work. Reaching it stops the trial; completion is not guaranteed.</p>
    {!prepared&&!pending&&<Button variant="outline" disabled={busy} onClick={prepare}>Review saved continuation recovery</Button>}
    {prepared&&!pending&&<Button disabled={busy} onClick={apply}>Resume under existing approval</Button>}
    {pending&&<><p className="text-sm">A recovery request is saved. Check the same request to confirm its outcome.</p><Button disabled={busy} onClick={apply}>Check saved recovery</Button></>}
    {error&&<p role="alert" className="text-sm text-red-600">{error}</p>}
  </div>;
}
